import React from 'react';
import { Button, Typography, theme } from 'antd';
import { ArrowRightOutlined, LockOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';

import { useAuth } from 'context/authProvider';
import { hasAnyPermission } from 'services/menu/permissionMapping';

const { Text } = Typography;

/*
| The "open the screen that does this" link on a support topic.
|
| Extracted so the support page and the assistant gate it identically. They render the
| same topics from the same corpus, and a rule this specific — show the answer to
| everyone, show the button only to someone who can use it — is exactly the kind that
| drifts when it is written twice.
|
| Linking someone to a page they cannot open is worse than not linking at all: they click,
| get a 403, and now believe the product is broken rather than that the task is not theirs.
| So a topic whose action is out of reach keeps its answer and swaps the button for who to
| ask.
|
| The permissions come from the API alongside the topic. They decide what to RENDER, never
| what is allowed — the endpoint behind the destination enforces that itself.
*/
const TopicAction = ({ action }) => {
    const { auth } = useAuth();
    const navigate = useNavigate();
    const { token } = theme.useToken();

    if (!action) return null;

    const userPermissions = auth?.user?.extra?.privileges?.permissions || [];
    const allowed = hasAnyPermission(action.permissions || [], userPermissions);

    if (!allowed) {
        return (
            <Text type="secondary" style={{ fontSize: token.fontSizeSM }}>
                <LockOutlined style={{ marginInlineEnd: 6 }} />
                An organization admin can do this for you.
            </Text>
        );
    }

    return (
        <Button
            type="link"
            size="small"
            style={{ paddingInline: 0 }}
            onClick={() => navigate(action.to)}
        >
            {action.label} <ArrowRightOutlined />
        </Button>
    );
};

export default TopicAction;
